import { cn } from '@/lib/utils';

export interface SubTabItem<T extends string> {
  value: T;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface ISubTabBarProps<T extends string> {
  tabs: readonly SubTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Underlined sub-tab navigation used inside a tab that has its own sub-sections
 * (Knowledge Base, Page CTAs). Keeps both tabs structurally identical.
 */
function SubTabBar<T extends string>({
  tabs, value, onChange, className,
}: ISubTabBarProps<T>) {
  return (
    <div className={cn('flex flex-shrink-0 gap-1 overflow-x-auto border-b border-gray-200 px-6 pt-4 scrollbar-none', className)}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = value === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={cn(
              'flex items-center gap-1.5 -mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
            )}
          >
            {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default SubTabBar;
